import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { useGeneration } from "@/hooks/useGeneration";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { characterService, AICharacter } from "@/lib/character-service";
import {
    Wand2,
    Sparkles,
    Play,
    Loader2,
    User,
    Shuffle,
    ExternalLink,
    History as HistoryIcon,
    Coins,
    Languages,
    Users,
    FolderOpen,
    Hash,
    AtSign,
} from "lucide-react";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

const QUANTITY_OPTIONS = [1, 5, 10, 20, 50, 100];

const LANGUAGE_OPTIONS = [
    { value: "none", label: "ไม่พูด", suffix: "" },
    { value: "thai", label: "ไทย", suffix: " The character speaks Thai language clearly." },
    { value: "english", label: "อังกฤษ", suffix: " The character speaks English clearly." },
    { value: "indian", label: "อินเดีย", suffix: " The character speaks Hindi clearly." },
    { value: "russian", label: "รัสเซีย", suffix: " The character speaks Russian clearly." },
];

const GENDER_OPTIONS = [
    { value: "male", label: "ชาย", labelEn: "Male", desc: "man" },
    { value: "female", label: "หญิง", labelEn: "Female", desc: "woman" },
    { value: "random", label: "สุ่ม", labelEn: "Random", desc: null },
];

// Random character generation
const AGES = Array.from({ length: 43 }, (_, i) => i + 18); // 18-60

const generateRandomCharacterDescription = (genderValue: string) => {
    let gender: string;
    if (genderValue === "random") {
        gender = Math.random() < 0.5 ? "man" : "woman";
    } else {
        const genderOpt = GENDER_OPTIONS.find(g => g.value === genderValue);
        gender = genderOpt?.desc || "person";
    }
    const age = AGES[Math.floor(Math.random() * AGES.length)];
    return `A ${age}-year-old ${gender}`;
};

// LocalStorage key for saving preferences
const MAGIC_PREFS_KEY = "magic_button_prefs";

interface MagicPrefs {
    characterId: string;
    category: string;
    language: string;
    quantity: number;
    gender: string;
}

interface Cameo {
    id: string;
    username: string;
}

const MagicButton = () => {
    const { t } = useLanguage();
    const { user } = useAuth();
    const { userCredits } = useGeneration();
    const { toast } = useToast();

    // State
    const [characters, setCharacters] = useState<AICharacter[]>([]);
    const [cameos, setCameos] = useState<Cameo[]>([]);
    const [categories, setCategories] = useState<string[]>([]);
    const [loadingCharacters, setLoadingCharacters] = useState(true);
    const [loadingCategories, setLoadingCategories] = useState(true);
    const [availablePromptsCount, setAvailablePromptsCount] = useState<number | null>(null);
    const [loadingPromptsCount, setLoadingPromptsCount] = useState(false);

    // Selection state - will be loaded from localStorage
    const [selectedCharacterId, setSelectedCharacterId] = useState<string>("random");
    const [selectedGender, setSelectedGender] = useState<string>("random");
    const [selectedCategory, setSelectedCategory] = useState<string>("");
    const [selectedLanguage, setSelectedLanguage] = useState<string>("thai");
    const [selectedQuantity, setSelectedQuantity] = useState<number>(10);
    const [prefsLoaded, setPrefsLoaded] = useState(false);

    // Submission
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Video model - fixed to Seedance 2.0 15s (Magic Button premium price for curated prompts)
    const videoModel = { id: "seedance2-15s", name: "Seedance 2.0 Fast (15s)", credits: 495 };

    // Load saved preferences from localStorage
    useEffect(() => {
        if (!user) return;
        try {
            const saved = localStorage.getItem(`${MAGIC_PREFS_KEY}_${user.id}`);
            if (saved) {
                const prefs: MagicPrefs = JSON.parse(saved);
                if (prefs.characterId) setSelectedCharacterId(prefs.characterId);
                if (prefs.gender) setSelectedGender(prefs.gender);
                if (prefs.language) setSelectedLanguage(prefs.language);
                if (prefs.quantity) setSelectedQuantity(prefs.quantity);
                // Category will be set after categories are loaded
                if (prefs.category) {
                    // Store temporarily for use when categories load
                    (window as any).__magicPrefCategory = prefs.category;
                }
            }
        } catch (e) {
            console.error("Error loading magic prefs:", e);
        }
        setPrefsLoaded(true);
    }, [user]);

    // Save preferences to localStorage whenever they change
    useEffect(() => {
        if (!user || !prefsLoaded) return;
        const prefs: MagicPrefs = {
            characterId: selectedCharacterId,
            gender: selectedGender,
            category: selectedCategory,
            language: selectedLanguage,
            quantity: selectedQuantity,
        };
        try {
            localStorage.setItem(`${MAGIC_PREFS_KEY}_${user.id}`, JSON.stringify(prefs));
        } catch (e) {
            console.error("Error saving magic prefs:", e);
        }
    }, [user, selectedCharacterId, selectedGender, selectedCategory, selectedLanguage, selectedQuantity, prefsLoaded]);

    // Load characters
    useEffect(() => {
        const loadCharacters = async () => {
            if (!user) return;
            setLoadingCharacters(true);
            try {
                const chars = await characterService.getMyCharacters();
                setCharacters(chars);
            } catch (error) {
                console.error("Error loading characters:", error);
            } finally {
                setLoadingCharacters(false);
            }
        };
        loadCharacters();
    }, [user]);

    // Load cameos from localStorage (same format as Sora.tsx)
    useEffect(() => {
        if (!user) return;
        try {
            const stored = localStorage.getItem(`sora_cameos_${user.id}`);
            if (stored) {
                setCameos(JSON.parse(stored));
            }
        } catch (e) {
            console.error("Error loading cameos:", e);
        }
    }, [user]);

    // Load categories from magic_prompts
    useEffect(() => {
        const loadCategories = async () => {
            setLoadingCategories(true);
            try {
                const { data, error } = await supabase
                    .from("magic_prompts")
                    .select("category")
                    .eq("is_active", true);

                if (error) throw error;

                const uniqueCategories = [...new Set((data || []).map(p => p.category))].filter(Boolean);
                setCategories(uniqueCategories);

                // Check if we have a saved category preference
                const savedCategory = (window as any).__magicPrefCategory;
                if (savedCategory && uniqueCategories.includes(savedCategory)) {
                    setSelectedCategory(savedCategory);
                    delete (window as any).__magicPrefCategory;
                } else if (uniqueCategories.length > 0 && !selectedCategory) {
                    setSelectedCategory(uniqueCategories[0]);
                }
            } catch (error) {
                console.error("Error loading categories:", error);
            } finally {
                setLoadingCategories(false);
            }
        };
        loadCategories();
    }, []);

    // Get gender for RPC calls (random = null means get any gender)
    const getGenderForRPC = () => {
        if (selectedGender === 'random') return null;
        return selectedGender; // 'male' or 'female'
    };

    // Fetch available prompts count when category or gender changes
    useEffect(() => {
        const fetchAvailableCount = async () => {
            if (!user || !selectedCategory) {
                setAvailablePromptsCount(null);
                return;
            }
            setLoadingPromptsCount(true);
            try {
                const genderParam = getGenderForRPC();
                const { data, error } = await supabase.rpc('get_available_magic_prompts_count', {
                    p_user_id: user.id,
                    p_category: selectedCategory,
                    p_gender: genderParam
                });
                if (error) throw error;
                setAvailablePromptsCount(data);
            } catch (error) {
                console.error("Error fetching available prompts count:", error);
                setAvailablePromptsCount(null);
            } finally {
                setLoadingPromptsCount(false);
            }
        };
        fetchAvailableCount();
    }, [user, selectedCategory, selectedGender]);

    // Calculate required credits
    const requiredCredits = useMemo(() => {
        return videoModel.credits * selectedQuantity;
    }, [selectedQuantity]);

    // Check if selected character is a cameo
    const isCameoSelected = selectedCharacterId.startsWith("cameo_");

    // Get selected character description or cameo username
    const getCharacterForPrompt = () => {
        if (selectedCharacterId === "random") {
            return null; // Will be generated per prompt
        }

        // Check if it's a cameo
        if (isCameoSelected) {
            const cameo = cameos.find(c => c.id === selectedCharacterId);
            return cameo?.username || null;
        }

        // It's an AI character
        const char = characters.find(c => c.id === selectedCharacterId);
        if (char?.metadata?.description) {
            return char.metadata.description;
        }
        // Fallback to name
        return char?.name || char?.alias || "A person";
    };

    // Fetch random available prompts from selected category using RPC
    const fetchRandomPrompts = async (count: number): Promise<{ id: string; prompt: string }[]> => {
        if (!user) throw new Error("User not logged in");

        const genderParam = getGenderForRPC();
        const { data, error } = await supabase.rpc('get_random_available_magic_prompts', {
            p_user_id: user.id,
            p_category: selectedCategory,
            p_count: count,
            p_gender: genderParam
        });

        if (error) throw error;
        if (!data || data.length === 0) {
            throw new Error(t("ไม่พบพรอมต์ที่ยังไม่ได้ใช้ในหมวดหมู่นี้", "No unused prompts found in this category"));
        }

        if (data.length < count) {
            throw new Error(t(
                `มีพรอมต์คงเหลือเพียง ${data.length} รายการ กรุณาลดจำนวนวิดีโอ`,
                `Only ${data.length} prompts remaining. Please reduce quantity.`
            ));
        }

        return data.map((p: { id: string; prompt: string }) => ({
            id: p.id,
            prompt: p.prompt
        }));
    };

    // Mark prompts as used after successful job creation
    const markPromptsAsUsed = async (promptIds: string[], bulkJobId: string) => {
        if (!user) return;
        try {
            const { error } = await supabase.rpc('mark_magic_prompts_used', {
                p_user_id: user.id,
                p_prompt_ids: promptIds,
                p_bulk_job_id: bulkJobId
            });
            if (error) {
                console.error("Error marking prompts as used:", error);
            }
        } catch (e) {
            console.error("Error marking prompts as used:", e);
        }
    };

    // Process prompt - replace @character and add language suffix
    const processPrompt = (prompt: string, characterValue: string | null): string => {
        let processed = prompt;

        // For cameos, use the @username directly
        // For characters, use the description
        // For random, generate a random description with selected gender
        const charDesc = characterValue || generateRandomCharacterDescription(selectedGender);

        // Replace various character placeholders
        processed = processed.replace(/@character/gi, charDesc);
        processed = processed.replace(/@ตัวละคร/gi, charDesc);

        // Add language suffix
        const langOption = LANGUAGE_OPTIONS.find(l => l.value === selectedLanguage);
        if (langOption?.suffix) {
            processed = processed + langOption.suffix;
        }

        return processed;
    };

    // Submit magic bulk job
    const handleSubmit = async () => {
        if (!user) {
            toast({
                variant: "destructive",
                title: t("กรุณาเข้าสู่ระบบ", "Please login"),
            });
            return;
        }

        if (!selectedCategory) {
            toast({
                variant: "destructive",
                title: t("กรุณาเลือกหมวดหมู่", "Please select a category"),
            });
            return;
        }

        if ((userCredits || 0) < requiredCredits) {
            toast({
                variant: "destructive",
                title: t("เครดิตไม่พอ", "Insufficient credits"),
                description: t(
                    `ต้องการ ${requiredCredits} เครดิต แต่คุณมี ${userCredits || 0} เครดิต`,
                    `Need ${requiredCredits} credits but you have ${userCredits || 0} credits`
                ),
            });
            return;
        }

        // Check if enough prompts are available
        if (availablePromptsCount !== null && availablePromptsCount < selectedQuantity) {
            toast({
                variant: "destructive",
                title: t("พรอมต์ไม่พอ", "Not enough prompts"),
                description: t(
                    `มีพรอมต์คงเหลือเพียง ${availablePromptsCount} รายการ กรุณาลดจำนวนวิดีโอ`,
                    `Only ${availablePromptsCount} prompts remaining. Please reduce quantity.`
                ),
            });
            return;
        }

        setIsSubmitting(true);

        try {
            // 1. Fetch random available prompts (returns id and prompt)
            const promptsData = await fetchRandomPrompts(selectedQuantity);

            // 2. Process prompts - replace character and add language
            const characterValue = getCharacterForPrompt();
            const processedPrompts = promptsData.map(p => ({
                id: p.id,
                processed: processPrompt(p.prompt, characterValue)
            }));

            // 3. Create bulk video job
            const { data: job, error: jobError } = await supabase
                .from("bulk_video_jobs" as any)
                .insert({
                    user_id: user.id,
                    model_id: videoModel.id,
                    model_name: `Magic: ${selectedCategory}`,
                    ratio: "9:16", // Default portrait for social media
                    total_items: selectedQuantity,
                    status: 'pending'
                })
                .select()
                .single();

            if (jobError) throw jobError;

            const jobId = (job as any).id;

            // 4. Insert items
            const items = processedPrompts.map(p => ({
                job_id: jobId,
                user_id: user.id,
                prompt: p.processed,
                status: 'pending'
            }));

            const { error: itemsError } = await supabase
                .from("bulk_video_items" as any)
                .insert(items);

            if (itemsError) throw itemsError;

            // 5. Mark prompts as used
            const promptIds = processedPrompts.map(p => p.id);
            await markPromptsAsUsed(promptIds, jobId);

            // 6. Update available prompts count
            setAvailablePromptsCount(prev => prev !== null ? Math.max(0, prev - selectedQuantity) : null);

            toast({
                title: t("ส่งงานสำเร็จ!", "Job submitted!"),
                description: t(
                    `สร้าง ${selectedQuantity} วิดีโอแบบสุ่มจากหมวด ${selectedCategory}`,
                    `Creating ${selectedQuantity} random videos from ${selectedCategory} category`
                ),
            });

            // 7. Trigger bulk processing
            await triggerBulkProcessing();

        } catch (error: any) {
            console.error("Magic button error:", error);
            toast({
                variant: "destructive",
                title: t("เกิดข้อผิดพลาด", "Error occurred"),
                description: error.message,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const triggerBulkProcessing = async () => {
        try {
            const session = await supabase.auth.getSession();
            const token = session.data.session?.access_token;
            if (!token) return;

            const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/process-bulk-video`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                console.error("Bulk processing trigger failed");
            }
        } catch (e) {
            console.error("Bulk processing trigger error:", e);
        }
    };

    // Get display name for selected character
    const getSelectedDisplayName = () => {
        if (selectedCharacterId === "random") {
            const genderOpt = GENDER_OPTIONS.find(g => g.value === selectedGender);
            const genderLabel = genderOpt ? t(genderOpt.label, genderOpt.labelEn) : "";
            return `${t("สุ่มตัวละคร", "Random")} (${genderLabel})`;
        }
        if (isCameoSelected) {
            const cameo = cameos.find(c => c.id === selectedCharacterId);
            return cameo?.username || "Cameo";
        }
        const char = characters.find(c => c.id === selectedCharacterId);
        return char?.name || char?.alias || "Character";
    };

    return (
        <div className="min-h-screen bg-background">
            <Navbar />

            <main className="container mx-auto px-4 py-8 mt-16 md:mt-20">
                <div className="flex flex-col gap-6 max-w-2xl mx-auto">
                    {/* Header */}
                    <div className="text-center space-y-3">
                        <div className="flex justify-center">
                            <div className="p-4 rounded-full bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30">
                                <Wand2 className="h-12 w-12 text-purple-400" />
                            </div>
                        </div>
                        <h1 className="text-3xl font-heading font-bold flex items-center justify-center gap-2">
                            <Sparkles className="h-8 w-8 text-yellow-400" />
                            {t("Magic Button", "Magic Button")}
                            <Sparkles className="h-8 w-8 text-yellow-400" />
                        </h1>
                        <p className="text-muted-foreground">
                            {t(
                                "สุ่มสร้างวิดีโอไวรัลอัตโนมัติ เพียงเลือกตัวละคร หมวดหมู่ และจำนวน!",
                                "Auto-generate viral videos! Just select character, category, and quantity!"
                            )}
                        </p>
                    </div>

                    {/* Main Card */}
                    <Card variant="glass" className="border-purple-500/20">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Wand2 className="h-5 w-5 text-purple-400" />
                                {t("ตั้งค่าการสุ่ม", "Magic Settings")}
                            </CardTitle>
                            <CardDescription>
                                {t("เลือกตัวละคร/Cameo หมวด ภาษา และจำนวนวิดีโอที่ต้องการ", "Select character/Cameo, category, language, and quantity")}
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {/* Character Selection */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium flex items-center gap-2">
                                    <User className="h-4 w-4 text-blue-400" />
                                    {t("ตัวละคร / Cameo", "Character / Cameo")}
                                </label>
                                <Select value={selectedCharacterId} onValueChange={setSelectedCharacterId}>
                                    <SelectTrigger>
                                        <SelectValue placeholder={t("เลือกตัวละคร", "Select character")}>
                                            <div className="flex items-center gap-2">
                                                {selectedCharacterId === "random" ? (
                                                    <Shuffle className="h-4 w-4 text-purple-400" />
                                                ) : isCameoSelected ? (
                                                    <AtSign className="h-4 w-4 text-pink-500" />
                                                ) : (
                                                    <User className="h-4 w-4 text-blue-400" />
                                                )}
                                                <span>{getSelectedDisplayName()}</span>
                                            </div>
                                        </SelectValue>
                                    </SelectTrigger>
                                    <SelectContent>
                                        {/* Random option */}
                                        <SelectItem value="random">
                                            <div className="flex items-center gap-2">
                                                <Shuffle className="h-4 w-4 text-purple-400" />
                                                <span>{t("สุ่มตัวละคร (ชาย/หญิง อายุ 18-60)", "Random (Male/Female, Age 18-60)")}</span>
                                            </div>
                                        </SelectItem>

                                        {/* Cameos Section */}
                                        {cameos.length > 0 && (
                                            <>
                                                <div className="px-2 py-1.5 text-xs font-semibold text-pink-500 bg-pink-500/10 mt-1">
                                                    Cameo ({cameos.length})
                                                </div>
                                                {cameos.map(cameo => (
                                                    <SelectItem key={cameo.id} value={cameo.id}>
                                                        <div className="flex items-center gap-2">
                                                            <AtSign className="h-4 w-4 text-pink-500" />
                                                            <span className="font-mono text-pink-500">{cameo.username}</span>
                                                        </div>
                                                    </SelectItem>
                                                ))}
                                            </>
                                        )}

                                        {/* AI Characters Section */}
                                        {loadingCharacters ? (
                                            <SelectItem value="loading" disabled>
                                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                                {t("กำลังโหลด...", "Loading...")}
                                            </SelectItem>
                                        ) : characters.length > 0 ? (
                                            <>
                                                <div className="px-2 py-1.5 text-xs font-semibold text-blue-500 bg-blue-500/10 mt-1">
                                                    AI Characters ({characters.length})
                                                </div>
                                                {characters.map(char => (
                                                    <SelectItem key={char.id} value={char.id}>
                                                        <div className="flex items-center gap-2">
                                                            {char.preview_url ? (
                                                                <img src={char.preview_url} alt="" className="h-6 w-6 rounded-full object-cover" />
                                                            ) : (
                                                                <User className="h-4 w-4 text-blue-400" />
                                                            )}
                                                            <span>{char.name || char.alias}</span>
                                                        </div>
                                                    </SelectItem>
                                                ))}
                                            </>
                                        ) : null}
                                    </SelectContent>
                                </Select>
                                <div className="flex justify-end">
                                    <Button variant="ghost" size="sm" className="text-xs gap-1" asChild>
                                        <Link to="/generate/sora/characters">
                                            <Users className="h-3.5 w-3.5" />
                                            {t("จัดการตัวละคร / Cameo", "Manage Characters / Cameo")}
                                            <ExternalLink className="h-3 w-3" />
                                        </Link>
                                    </Button>
                                </div>
                            </div>

                            {/* Gender Selection - shown when random character is selected */}
                            {selectedCharacterId === "random" && (
                                <div className="space-y-2">
                                    <label className="text-sm font-medium flex items-center gap-2">
                                        <Users className="h-4 w-4 text-purple-400" />
                                        {t("เพศตัวละคร", "Character Gender")}
                                    </label>
                                    <div className="grid grid-cols-3 gap-2">
                                        {GENDER_OPTIONS.map(opt => (
                                            <Button
                                                key={opt.value}
                                                variant={selectedGender === opt.value ? "default" : "outline"}
                                                className={selectedGender === opt.value ? "bg-purple-600 hover:bg-purple-700" : ""}
                                                onClick={() => setSelectedGender(opt.value)}
                                            >
                                                {t(opt.label, opt.labelEn)}
                                            </Button>
                                        ))}
                                    </div>
                                    <p className="text-xs text-muted-foreground">
                                        {t("เลือกเพศสำหรับสุ่มตัวละคร (prompt จะแตกต่างกันตามเพศ)", "Select gender for random character (prompts differ by gender)")}
                                    </p>
                                </div>
                            )}

                            {/* Category Selection */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium flex items-center gap-2">
                                    <FolderOpen className="h-4 w-4 text-orange-400" />
                                    {t("หมวดหมู่พรอมต์", "Prompt Category")}
                                </label>
                                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                                    <SelectTrigger>
                                        <SelectValue placeholder={t("เลือกหมวดหมู่", "Select category")} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {loadingCategories ? (
                                            <SelectItem value="loading" disabled>
                                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                                {t("กำลังโหลด...", "Loading...")}
                                            </SelectItem>
                                        ) : categories.length === 0 ? (
                                            <SelectItem value="none" disabled>
                                                {t("ไม่พบหมวดหมู่", "No categories found")}
                                            </SelectItem>
                                        ) : (
                                            categories.map(cat => (
                                                <SelectItem key={cat} value={cat}>
                                                    {cat}
                                                </SelectItem>
                                            ))
                                        )}
                                    </SelectContent>
                                </Select>
                                {/* Available prompts count */}
                                {selectedCategory && (
                                    <div className="flex items-center gap-2 text-sm">
                                        {loadingPromptsCount ? (
                                            <span className="text-muted-foreground flex items-center gap-1">
                                                <Loader2 className="h-3 w-3 animate-spin" />
                                                {t("กำลังโหลด...", "Loading...")}
                                            </span>
                                        ) : availablePromptsCount !== null ? (
                                            <span className={availablePromptsCount < selectedQuantity ? "text-red-400" : "text-green-400"}>
                                                {t(
                                                    `พรอมต์คงเหลือ: ${availablePromptsCount} รายการ`,
                                                    `Available prompts: ${availablePromptsCount}`
                                                )}
                                                {availablePromptsCount < selectedQuantity && (
                                                    <span className="text-red-400 ml-2">
                                                        ({t("ไม่พอสำหรับจำนวนที่เลือก", "not enough for selected quantity")})
                                                    </span>
                                                )}
                                            </span>
                                        ) : null}
                                    </div>
                                )}
                            </div>

                            {/* Language Selection */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium flex items-center gap-2">
                                    <Languages className="h-4 w-4 text-green-400" />
                                    {t("ภาษาในคลิป", "Video Language")}
                                </label>
                                <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {LANGUAGE_OPTIONS.map(lang => (
                                            <SelectItem key={lang.value} value={lang.value}>
                                                {lang.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {/* Quantity Selection */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium flex items-center gap-2">
                                    <Hash className="h-4 w-4 text-cyan-400" />
                                    {t("จำนวนวิดีโอ", "Number of Videos")}
                                </label>
                                <div className="grid grid-cols-6 gap-2">
                                    {QUANTITY_OPTIONS.map(qty => (
                                        <Button
                                            key={qty}
                                            variant={selectedQuantity === qty ? "default" : "outline"}
                                            className={selectedQuantity === qty ? "bg-purple-600 hover:bg-purple-700" : ""}
                                            onClick={() => setSelectedQuantity(qty)}
                                        >
                                            {qty}
                                        </Button>
                                    ))}
                                </div>
                            </div>

                            {/* Summary */}
                            <div className="p-4 rounded-xl bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 space-y-3">
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-muted-foreground">{t("โมเดล", "Model")}</span>
                                    <Badge variant="secondary">{videoModel.name}</Badge>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-muted-foreground">{t("อัตราส่วน", "Aspect Ratio")}</span>
                                    <Badge variant="outline">9:16 ({t("แนวตั้ง", "Portrait")})</Badge>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-muted-foreground">{t("ตัวละคร", "Character")}</span>
                                    <Badge variant={isCameoSelected ? "default" : "outline"} className={isCameoSelected ? "bg-pink-500" : ""}>
                                        {isCameoSelected && <AtSign className="h-3 w-3 mr-1" />}
                                        {getSelectedDisplayName()}
                                    </Badge>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-muted-foreground">{t("จำนวน", "Quantity")}</span>
                                    <span className="font-bold">{selectedQuantity} {t("คลิป", "clips")}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-muted-foreground">{t("พรอมต์คงเหลือ", "Prompts left")}</span>
                                    {loadingPromptsCount ? (
                                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                    ) : (
                                        <Badge variant={availablePromptsCount !== null && availablePromptsCount < selectedQuantity ? "destructive" : "outline"}>
                                            {availablePromptsCount !== null ? availablePromptsCount : "-"}
                                        </Badge>
                                    )}
                                </div>
                                <div className="border-t border-white/10 pt-3 flex justify-between items-center">
                                    <span className="text-sm font-medium flex items-center gap-2">
                                        <Coins className="h-4 w-4 text-yellow-400" />
                                        {t("เครดิตที่ใช้", "Credits needed")}
                                    </span>
                                    <span className="font-bold text-lg text-purple-400">
                                        {requiredCredits.toLocaleString()} Cr.
                                    </span>
                                </div>
                                {(userCredits || 0) < requiredCredits && (
                                    <div className="text-xs text-red-400 flex items-center gap-1">
                                        <span>{t("คุณมี", "You have")} {userCredits || 0} Cr.</span>
                                        <Link to="/pricing" className="underline">{t("เติมเครดิต", "Buy credits")}</Link>
                                    </div>
                                )}
                                {availablePromptsCount !== null && availablePromptsCount < selectedQuantity && (
                                    <div className="text-xs text-red-400">
                                        {t(
                                            `พรอมต์ไม่พอ! เหลือเพียง ${availablePromptsCount} รายการ กรุณาลดจำนวนวิดีโอ`,
                                            `Not enough prompts! Only ${availablePromptsCount} remaining. Please reduce quantity.`
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Submit Button */}
                            <Button
                                size="lg"
                                className="w-full h-14 text-lg gap-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 shadow-lg shadow-purple-500/25"
                                onClick={handleSubmit}
                                disabled={
                                    isSubmitting ||
                                    !selectedCategory ||
                                    (userCredits || 0) < requiredCredits ||
                                    (availablePromptsCount !== null && availablePromptsCount < selectedQuantity)
                                }
                            >
                                {isSubmitting ? (
                                    <>
                                        <Loader2 className="h-6 w-6 animate-spin" />
                                        {t("กำลังส่งงาน...", "Submitting...")}
                                    </>
                                ) : (
                                    <>
                                        <Wand2 className="h-6 w-6" />
                                        {t("สร้างวิดีโอสุ่ม", "Generate Random Videos")}
                                        <Sparkles className="h-5 w-5" />
                                    </>
                                )}
                            </Button>

                            {/* Quick Links */}
                            <div className="flex justify-center gap-4 pt-2">
                                <Button variant="ghost" size="sm" className="text-xs gap-1" asChild>
                                    <Link to="/generate/videobulk">
                                        <HistoryIcon className="h-3.5 w-3.5" />
                                        {t("ดูสถานะงาน", "View Job Status")}
                                    </Link>
                                </Button>
                                <Button variant="ghost" size="sm" className="text-xs gap-1" asChild>
                                    <Link to="/history/video">
                                        <Play className="h-3.5 w-3.5" />
                                        {t("ดูวิดีโอทั้งหมด", "View All Videos")}
                                    </Link>
                                </Button>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Info Card */}
                    <Card variant="glass" className="border-blue-500/20">
                        <CardContent className="p-4">
                            <h3 className="font-medium mb-2 flex items-center gap-2">
                                <Sparkles className="h-4 w-4 text-blue-400" />
                                {t("วิธีใช้ Magic Button", "How Magic Button Works")}
                            </h3>
                            <ul className="text-sm text-muted-foreground space-y-1.5">
                                <li>1. {t("เลือกตัวละคร, Cameo หรือสุ่มตัวละครอัตโนมัติ", "Select character, Cameo, or use random auto-generation")}</li>
                                <li>2. {t("เลือกหมวดหมู่พรอมต์ที่ต้องการ (เช่น Lifestyle, Food, Travel)", "Choose a prompt category (e.g., Lifestyle, Food, Travel)")}</li>
                                <li>3. {t("เลือกภาษาในคลิป (ไม่พูด หรือเลือกภาษาที่ต้องการ)", "Select video language (silent or choose language)")}</li>
                                <li>4. {t("กำหนดจำนวนวิดีโอที่ต้องการสร้าง", "Set the number of videos to generate")}</li>
                                <li>5. {t("กดปุ่มสร้าง ระบบจะสุ่มพรอมต์และส่งงานอัตโนมัติ!", "Click generate - system will pick random prompts and submit!")}</li>
                            </ul>
                            <p className="text-xs text-muted-foreground/60 mt-3 italic">
                                {t("* ระบบจะจำค่าที่เลือกล่าสุดเป็นค่าเริ่มต้น", "* System remembers your last settings as default")}
                            </p>
                        </CardContent>
                    </Card>
                </div>
            </main>
        </div>
    );
};

export default MagicButton;
